import qrcode from './vendor/qrcode.mjs';

function encodeText(value) {
  return new TextEncoder().encode(value);
}

function concatBytes(parts) {
  const total = parts.reduce((sum, part) => sum + part.length, 0);
  const output = new Uint8Array(total);
  let offset = 0;
  parts.forEach((part) => {
    output.set(part, offset);
    offset += part.length;
  });
  return output;
}

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.decoding = 'async';
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error('The poster artwork could not be loaded.'));
    image.src = src;
  });
}

function drawContain(ctx, image, rect, padding = 0) {
  const availableWidth = Math.max(1, rect.width - (padding * 2));
  const availableHeight = Math.max(1, rect.height - (padding * 2));
  const scale = Math.min(availableWidth / image.naturalWidth, availableHeight / image.naturalHeight);
  const width = image.naturalWidth * scale;
  const height = image.naturalHeight * scale;
  const x = rect.x + ((rect.width - width) / 2);
  const y = rect.y + ((rect.height - height) / 2);
  ctx.drawImage(image, x, y, width, height);
}

export function createQrMatrix(value, errorCorrectionLevel = 'M') {
  if (!value) throw new Error('A patient link is required before a QR code can be generated.');
  const qr = qrcode(0, errorCorrectionLevel);
  qr.addData(String(value), 'Byte');
  qr.make();
  return qr;
}

export function drawQrCode(ctx, value, rect, options = {}) {
  const qr = createQrMatrix(value, options.errorCorrectionLevel || 'M');
  const moduleCount = qr.getModuleCount();
  const quietModules = 4;
  const totalModules = moduleCount + (quietModules * 2);
  const cell = Math.max(1, Math.floor(Math.min(rect.width, rect.height) / totalModules));
  const size = cell * totalModules;
  const originX = Math.round(rect.x + ((rect.width - size) / 2));
  const originY = Math.round(rect.y + ((rect.height - size) / 2));

  ctx.save();
  // The approved masters now have completely blank QR boxes. Paint only the
  // QR square (including its quiet zone) so the poster's coloured border stays intact.
  ctx.fillStyle = options.light || '#ffffff';
  ctx.fillRect(originX, originY, size, size);
  ctx.fillStyle = options.dark || '#000000';

  for (let row = 0; row < moduleCount; row += 1) {
    for (let col = 0; col < moduleCount; col += 1) {
      if (!qr.isDark(row, col)) continue;
      ctx.fillRect(
        originX + ((col + quietModules) * cell),
        originY + ((row + quietModules) * cell),
        cell,
        cell,
      );
    }
  }
  ctx.restore();

  return { moduleCount, size, cell };
}

export async function readImageFile(file) {
  if (!file) return null;
  if (!/^image\/(png|jpeg|webp|svg\+xml)$/i.test(file.type || '')) {
    throw new Error('Use a PNG, JPG, WebP or SVG Trust logo.');
  }
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ''));
    reader.onerror = () => reject(new Error('The Trust logo could not be read.'));
    reader.readAsDataURL(file);
  });
}

export async function renderPoster(canvas, { template, targetUrl, logoDataUrl = null }) {
  if (!canvas || !template) return null;
  const { width, height } = template.sourceDimensions;
  canvas.width = width;
  canvas.height = height;

  const ctx = canvas.getContext('2d', { alpha: false });
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';

  const poster = await loadImage(template.posterSrc);
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, width, height);
  ctx.drawImage(poster, 0, 0, width, height);

  drawQrCode(ctx, targetUrl, template.qrRect, { errorCorrectionLevel: 'M' });

  if (logoDataUrl) {
    const logo = await loadImage(logoDataUrl);
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(
      template.logoRect.x,
      template.logoRect.y,
      template.logoRect.width,
      template.logoRect.height,
    );
    drawContain(ctx, logo, template.logoRect, template.logoRect.padding || 0);
  }

  return canvas;
}

function triggerDownload(blob, filename) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 1500);
}

export async function downloadCanvasPng(canvas, filename) {
  const blob = await new Promise((resolve, reject) => {
    canvas.toBlob((result) => result ? resolve(result) : reject(new Error('PNG export failed.')), 'image/png');
  });
  triggerDownload(blob, filename);
}

export async function downloadQrPng(targetUrl, filename, size = 1200) {
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d', { alpha: false });
  drawQrCode(ctx, targetUrl, { x: 0, y: 0, width: size, height: size }, { errorCorrectionLevel: 'M' });
  await downloadCanvasPng(canvas, filename);
}

export async function canvasToA4PdfBlob(canvas) {
  const jpegBlob = await new Promise((resolve, reject) => {
    canvas.toBlob((result) => result ? resolve(result) : reject(new Error('PDF image export failed.')), 'image/jpeg', 0.98);
  });
  const jpeg = new Uint8Array(await jpegBlob.arrayBuffer());
  const pageWidth = 595.28;
  const pageHeight = 841.89;

  const contentStream = `q\n${pageWidth} 0 0 ${pageHeight} 0 0 cm\n/Im0 Do\nQ\n`;
  const objectParts = [
    [encodeText('1 0 obj\n<< /Type /Catalog /Pages 2 0 R >>\nendobj\n')],
    [encodeText('2 0 obj\n<< /Type /Pages /Kids [3 0 R] /Count 1 >>\nendobj\n')],
    [encodeText(`3 0 obj\n<< /Type /Page /Parent 2 0 R /MediaBox [0 0 ${pageWidth} ${pageHeight}] /Resources << /XObject << /Im0 4 0 R >> >> /Contents 5 0 R >>\nendobj\n`)],
    [
      encodeText(`4 0 obj\n<< /Type /XObject /Subtype /Image /Width ${canvas.width} /Height ${canvas.height} /ColorSpace /DeviceRGB /BitsPerComponent 8 /Filter /DCTDecode /Length ${jpeg.length} >>\nstream\n`),
      jpeg,
      encodeText('\nendstream\nendobj\n'),
    ],
    [
      encodeText(`5 0 obj\n<< /Length ${encodeText(contentStream).length} >>\nstream\n${contentStream}endstream\nendobj\n`),
    ],
  ];

  const header = encodeText('%PDF-1.4\n%KYC\n');
  const chunks = [header];
  const offsets = [0];
  let byteOffset = header.length;

  objectParts.forEach((parts) => {
    offsets.push(byteOffset);
    parts.forEach((part) => {
      chunks.push(part);
      byteOffset += part.length;
    });
  });

  const xrefOffset = byteOffset;
  let xref = 'xref\n0 6\n0000000000 65535 f \n';
  for (let i = 1; i <= 5; i += 1) {
    xref += `${String(offsets[i]).padStart(10, '0')} 00000 n \n`;
  }
  xref += `trailer\n<< /Size 6 /Root 1 0 R >>\nstartxref\n${xrefOffset}\n%%EOF\n`;
  chunks.push(encodeText(xref));

  return new Blob([concatBytes(chunks)], { type: 'application/pdf' });
}

export async function downloadCanvasPdf(canvas, filename) {
  const blob = await canvasToA4PdfBlob(canvas);
  triggerDownload(blob, filename);
}

export function safeFilename(value) {
  return String(value || 'know-your-care')
    .trim()
    .replace(/[^a-z0-9]+/gi, '-')
    .replace(/^-+|-+$/g, '')
    .toLowerCase();
}
